import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, RefreshCw, MessageSquare, Loader2, Camera, User } from 'lucide-react';
import { useMuro } from '../hooks/useMuro';
import { PostCard } from '../components/muro/PostCard';
import { UploadModal } from '../components/muro/UploadModal';
import { CommentSection } from '../components/muro/CommentSection';
import { Storage } from '../lib/storage';
import { supabase } from '../lib/supabase';
import { ConfirmActionModal } from '../components/ui/ConfirmActionModal';

export default function MuroBikerScreen() {
  const { 
    posts, 
    loading, 
    refreshing, 
    loadingMas,
    hayMas,
    fetchPosts, 
    toggleLike, 
    checkRestrictions, 
    publicarPost,
    eliminarPost,
    reportarPost,
    setRefreshing 
  } = useMuro();
  const [showUpload, setShowUpload] = useState(false);
  const [filterMine, setFilterMine] = useState(false);
  const [postToDelete, setPostToDelete] = useState<string | null>(null);
  const [activePostForComments, setActivePostForComments] = useState<string | null>(null);
  const userId = Storage.get('gravity_user_id');

  useEffect(() => {
    fetchPosts(true, filterMine, '');
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchPosts(true, filterMine, '');
  };

  const handleDeletePost = async () => {
    if (!postToDelete) return;
    const result = await eliminarPost(postToDelete);
    if (result.success) {
      fetchPosts();
    } else {
      alert('Error al eliminar: ' + result.error);
    }
  };

  const handleReportPost = async (id: string) => {
    const result = await reportarPost(id);
    if (result.success) {
      if (result.wasDeleted) {
        alert('La publicación ha sido eliminada automáticamente por acumulación de reportes.');
      } else {
        alert('Gracias por tu reporte. La comunidad MotoPikis revisará este contenido.');
      }
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (loading || loadingMas || !hayMas) return;
    
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 100) {
      fetchPosts(false, filterMine, '');
    }
  };

  const toggleFilter = () => {
    const nextFilter = !filterMine;
    setFilterMine(nextFilter);
    fetchPosts(true, nextFilter, '');
  };

  return (
    <div 
      onScroll={handleScroll}
      className="flex flex-col h-full relative bg-transparent px-5 pt-6 pb-24 overflow-y-auto scrollbar-hide"
    >
      {/* CABECERA */}
      <div className="flex items-center justify-between mb-8 shrink-0">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-cyan/20 rounded-2xl">
            <Camera className="w-6 h-6 text-primary-cyan" />
          </div>
          <h2 className="text-xl font-black text-white uppercase tracking-tighter leading-none">Muro Bike<br/><span className="text-[10px] text-slate-500 tracking-[2px]">{filterMine ? 'Mis Publicaciones' : 'Comunidad VIP'}</span></h2>
        </div>
        
        <div className="flex items-center gap-2">
          <button 
            onClick={toggleFilter}
            className={`p-2 rounded-xl transition-all ${filterMine ? 'bg-primary-cyan text-bg-dark shadow-lg shadow-primary-cyan/30' : 'bg-white/5 text-slate-500'}`}
          >
            <User className="w-4 h-4" />
          </button>

          <button 
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 bg-white/5 rounded-xl text-slate-500 hover:text-white transition-colors disabled:opacity-30"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* FEED DE PUBLICACIONES */}
      <div className="flex-1 space-y-2">
        {loading && !refreshing ? (
          <div className="flex flex-col items-center justify-center py-20 opacity-30">
            <Loader2 className="w-8 h-8 text-primary-cyan animate-spin mb-3" />
            <span className="text-[10px] font-black uppercase tracking-widest">Sincronizando Muro...</span>
          </div>
        ) : posts.length > 0 ? (
          posts.map(post => (
            <PostCard 
              key={post.id} 
              post={post} 
              onLike={toggleLike} 
              onComment={id => setActivePostForComments(id)}
              onDelete={id => setPostToDelete(id)}
              onReport={handleReportPost}
              isOwner={post.usuario_id === userId}
            />
          ))
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center px-10">
            <div className="w-20 h-20 bg-slate-800/50 rounded-full flex items-center justify-center mb-4 border border-white/5">
              <MessageSquare className="w-8 h-8 text-slate-700" />
            </div>
            <h3 className="text-white font-black text-sm uppercase mb-1">
              {filterMine ? 'No tienes publicaciones' : 'Muro en Silencio'}
            </h3>
            <p className="text-slate-500 text-[11px] font-medium leading-tight">
              {filterMine ? 'Tus fotos compartidas aparecerán aquí.' : 'Sé el primero en compartir una foto de tu ruta hoy.'}
            </p>
          </div>
        )}

        {/* INDICADOR DE CARGA ADICIONAL */}
        {loadingMas && (
          <div className="flex justify-center py-4">
            <Loader2 className="w-6 h-6 text-primary-cyan animate-spin opacity-50" />
          </div>
        )}

        {!hayMas && posts.length > 0 && (
          <div className="flex flex-col items-center justify-center py-8 opacity-20">
            <div className="w-px h-8 bg-gradient-to-b from-white/20 to-transparent mb-4" />
            <span className="text-[9px] font-black uppercase tracking-[3px]">Fin de la Ruta</span>
          </div>
        )}
      </div>

      {/* BOTÓN FLOTANTE: PUBLICAR */}
      <motion.button 
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setShowUpload(true)}
        className="fixed bottom-28 right-6 w-14 h-14 bg-primary-cyan text-bg-dark rounded-full flex items-center justify-center shadow-2xl shadow-primary-cyan/40 z-40"
      >
        <Plus className="w-7 h-7" />
      </motion.button>

      {/* MODALES MODULARES */}
      <UploadModal 
        isOpen={showUpload} 
        onClose={() => setShowUpload(false)} 
        onSuccess={() => fetchPosts(true, filterMine, '')}
        checkRestrictions={checkRestrictions}
        publicarPost={publicarPost}
      />

      <CommentSection 
        postId={activePostForComments || ''} 
        isOpen={!!activePostForComments} 
        onClose={() => setActivePostForComments(null)}
        onCommentAdded={() => fetchPosts(true, filterMine, '')}
      />

      <ConfirmActionModal 
        isOpen={!!postToDelete}
        onClose={() => setPostToDelete(null)}
        onConfirm={handleDeletePost}
        title="¿Eliminar Post?"
        message="Esta acción borrará la foto del muro permanentemente. ¿Estás seguro?"
        confirmText="Sí, Eliminar"
        cancelText="No, Mantener"
        variant="danger"
      />
    </div>
  );
}
